'use client'

import React from 'react'
import { ChevronLeft } from 'lucide-react'
import { UserCircle } from 'solar-icons'
import { toast } from 'sonner'
import { paymentMethods } from '@/lib/config'
import {
  useCart,
  useCheckout,
  useModals,
  useSelectedCustomer,
} from '@/lib/hooks'
import { cn, formatPrice } from '@/lib/utils'

import { Button, PaymentMethod, RadioGroup } from '@/components/ui'

export const SelectPaymentMethod = ({
  isCusRequired,
}: {
  isCusRequired?: boolean
}) => {
  const { openModal } = useModals()
  const { cart, totalPayable } = useCart()
  const { customer } = useSelectedCustomer()
  const {
    setCurrentStep,
    setPaymentMethodType,
    setPaymentMethod,
    setLastDigits,
    payment_method_type,
    payment_methods,
    balance_to_paid,
    setPaymentMethods,
  } = useCheckout()

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  React.useEffect(() => {
    let cancelled = false
    const fetchPaymentMethods = async () => {
      await setPaymentMethods()
    }

    if (!payment_methods) {
      if (!cancelled) {
        fetchPaymentMethods()
      }
    }

    return () => {
      cancelled = true
    }
  }, [payment_methods])

  const handlePaymentMethod = (value: PaymentMethod) => {
    if (cart?.cart_items?.length === 0) {
      toast.error('Please add items to the cart before proceeding.')
      return
    }

    setPaymentMethodType(value)
    setLastDigits(null)

    switch (value) {
      case 'cash':
        setPaymentMethod(payment_methods?.cash?.[0])
        setCurrentStep('COLLECT_PAYMENT')
        break
      case 'card':
        setPaymentMethod(null)
        openModal('SELECT_PAYMENT_CARD_TYPE')
        break
      case 'pickme':
        setPaymentMethod(payment_methods?.other?.[6])
        setCurrentStep('COLLECT_PAYMENT')
        break
      case 'uber':
        setPaymentMethod(payment_methods?.other?.[5])
        setCurrentStep('COLLECT_PAYMENT')
        break
      case 'other':
        setPaymentMethod(null)
        openModal('SELECT_OTHER_PAYMENT_TYPE')
        break
      default:
        break
    }
  }

  return (
    <div className="flex h-full w-full flex-col items-center gap-10 pt-4">
      <div className="flex w-full flex-grow flex-col items-center gap-6 lg:gap-12">
        <div className="flex flex-col items-center gap-4">
          <h2 className="text-base font-medium">Select Payment Method</h2>
          <h1 className="font-mono text-3xl font-extrabold lg:text-5xl">
            {formatPrice(balance_to_paid, 2, true)}
          </h1>

          {balance_to_paid !== totalPayable && (
            <span className="text-lg font-medium line-through">
              {formatPrice(totalPayable, 2, true)}
            </span>
          )}
        </div>
        <form className="-mx-10 flex w-full flex-col items-center gap-6">
          <RadioGroup
            className="flex w-full flex-col items-center justify-center gap-4 lg:flex-row"
            onValueChange={handlePaymentMethod}
          >
            {paymentMethods.map((method) => (
              <PaymentMethod
                key={method}
                className="h-[100px] w-full flex-1 lg:w-auto"
                value={method}
                onClick={() => {
                  if (payment_method_type === method) {
                    handlePaymentMethod(method as PaymentMethod)
                  }
                }}
              />
            ))}
          </RadioGroup>
        </form>
      </div>

      {isCusRequired && (
        <div className="flex w-full flex-col gap-2">
          <Button
            variant="outline"
            className="w-full justify-center gap-2"
            size="lg"
            onClick={() => setCurrentStep('SELECT_CUSTOMER')}
          >
            <div
              className={cn(
                'flex w-full items-center',
                customer
                  ? 'justify-between text-left'
                  : 'justify-center text-center',
              )}
            >
              <div className="flex items-center gap-2">
                <ChevronLeft strokeWidth={1.2} className="size-5" />
                <span>Change Customer</span>
              </div>

              {customer && (
                <div className="flex items-center gap-2">
                  <div className="flex flex-col text-right">
                    <span className="text-xs font-medium">
                      {customer?.name}
                    </span>
                    <span className="text-xs font-medium text-gray-500">
                      {customer?.phone}
                    </span>
                  </div>
                  <UserCircle strokeWidth={1.2} className="size-5" />
                </div>
              )}
            </div>
          </Button>
        </div>
      )}
    </div>
  )
}
